import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { defaultSiteSettings, SiteSettings } from "../utils/siteSettings";
import { defaultSiteCopy, SiteCopy } from "../utils/siteCopy";

// Set axios base URL
const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
    ? ""
    : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

const ContactsContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 4rem 2rem;
    min-height: 60vh;
`;

const ContactsTitle = styled.h1`
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #333;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const ContactsSubtitle = styled.p`
    font-size: 1.1rem;
    color: #666;
    text-align: center;
    margin-bottom: 3rem;
    line-height: 1.6;
`;

const ContactForm = styled.form`
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e1e5e9;
    border-radius: 5px;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
        outline: none;
        border-color: #667eea;
    }

    &::placeholder {
        color: #999;
    }
`;

const Select = styled.select`
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e1e5e9;
    border-radius: 5px;
    font-size: 1rem;
    background: white;

    &:focus {
        outline: none;
        border-color: #667eea;
    }
`;

const TextArea = styled.textarea`
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e1e5e9;
    border-radius: 5px;
    font-size: 1rem;
    min-height: 120px;
    resize: vertical;
    transition: border-color 0.3s ease;

    &:focus {
        outline: none;
        border-color: #667eea;
    }

    &::placeholder {
        color: #999;
    }
`;

const SubmitButton = styled.button`
    background: #667eea;
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 5px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.3s ease;
    width: 100%;

    &:hover {
        background: #5a6fd8;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }

    @media (min-width: 768px) {
        width: auto;
    }
`;

const Message = styled.div<{ type: "success" | "error" }>`
    padding: 1rem;
    border-radius: 5px;
    margin-bottom: 1rem;
    text-align: center;
    background: ${(props) => (props.type === "success" ? "#d4edda" : "#f8d7da")};
    color: ${(props) => (props.type === "success" ? "#155724" : "#721c24")};
    border: 1px solid ${(props) => (props.type === "success" ? "#c3e6cb" : "#f5c6cb")};
`;

const ContactContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    margin-top: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const ContactInfo = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ContactInfoTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1.5rem;
`;

const CompanyName = styled.h4`
    font-size: 1.2rem;
    font-weight: 600;
    color: #667eea;
    margin-bottom: 1rem;
`;

const InfoItem = styled.div`
    margin-bottom: 1rem;
    line-height: 1.6;
`;

const InfoLabel = styled.span`
    font-weight: 600;
    color: #333;
    display: inline-block;
    min-width: 60px;
`;

const InfoValue = styled.span`
    color: #666;
`;

const MapContainer = styled.div`
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    height: 400px;
`;

const MapTitle = styled.h3`
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
    padding: 1rem;
    background: #f8f9fa;
    margin: 0;
`;

const MapContent = styled.div`
    padding: 2rem;
    text-align: center;
    height: calc(100% - 60px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const MapAddress = styled.p`
    color: #666;
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
`;

const MapButton = styled.a`
    display: inline-block;
    padding: 0.75rem 1.5rem;
    background: #667eea;
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-weight: 600;
    margin: 0.5rem;
    transition: background 0.3s ease;

    &:hover {
        background: #5a6fd8;
    }

    &:visited {
        color: white;
    }
`;

const MapButtonContainer = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
`;

const Contacts: React.FC = () => {
    const [siteSettings, setSiteSettings] = useState<SiteSettings>(defaultSiteSettings);
    const [siteCopy, setSiteCopy] = useState<SiteCopy>(defaultSiteCopy);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        projectType: "",
        schedule: "",
        budget: "",
        location: "",
        referenceUrl: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const response = await axios.get("/api/site-settings");
                const copyResponse = await axios.get("/api/site-copy").catch(() => ({ data: { data: defaultSiteCopy } }));
                setSiteSettings({ ...defaultSiteSettings, ...(response.data.data || {}) });
                setSiteCopy({ ...defaultSiteCopy, ...(copyResponse.data.data || {}) });
            } catch (error) {
                console.error("Error fetching site settings:", error);
            }
        };

        fetchSiteSettings();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage(null);

        try {
            const response = await axios.post("/api/contacts", formData);

            if (response.data.success) {
                setMessage({ type: "success", text: "메시지가 성공적으로 전송되었습니다!" });
                setFormData({ name: "", email: "", projectType: "", schedule: "", budget: "", location: "", referenceUrl: "", message: "" });
            } else {
                setMessage({ type: "error", text: response.data.error || "메시지 전송에 실패했습니다." });
            }
        } catch (error: any) {
            console.error("Error submitting contact form:", error);
            setMessage({
                type: "error",
                text: error.response?.data?.error || "메시지 전송 중 오류가 발생했습니다.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ContactsContainer>
            <ContactsTitle>{siteCopy.contacts_title}</ContactsTitle>
            <ContactsSubtitle>
                {siteCopy.contacts_subtitle.split("\n").map((line) => (
                    <React.Fragment key={line}>
                        {line}
                        <br />
                    </React.Fragment>
                ))}
            </ContactsSubtitle>

            <ContactContent>
                <div>
                    <ContactForm onSubmit={handleSubmit}>
                        {message && <Message type={message.type}>{message.text}</Message>}

                        <FormGroup>
                            <Label htmlFor="name">이름 *</Label>
                            <Input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="이름을 입력해주세요" required />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="email">이메일 *</Label>
                            <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="이메일을 입력해주세요" required />
                        </FormGroup>

                        {siteSettings.show_enhanced_contact_fields && (
                            <>
                                <FormGroup>
                                    <Label htmlFor="projectType">영상 유형</Label>
                                    <Select id="projectType" name="projectType" value={formData.projectType} onChange={handleChange}>
                                        <option value="">선택해주세요</option>
                                        <option value="브랜드 홍보 영상">브랜드 홍보 영상</option>
                                        <option value="제품 소개 영상">제품 소개 영상</option>
                                        <option value="숏폼/SNS 콘텐츠">숏폼/SNS 콘텐츠</option>
                                        <option value="행사/스케치 영상">행사/스케치 영상</option>
                                        <option value="인터뷰/기업 영상">인터뷰/기업 영상</option>
                                    </Select>
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="schedule">희망 일정</Label>
                                    <Input type="text" id="schedule" name="schedule" value={formData.schedule} onChange={handleChange} placeholder="예: 7월 중 촬영, 8월 초 납품" />
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="budget">예산 범위</Label>
                                    <Select id="budget" name="budget" value={formData.budget} onChange={handleChange}>
                                        <option value="">선택해주세요</option>
                                        <option value="100만원 이하">100만원 이하</option>
                                        <option value="100-300만원">100-300만원</option>
                                        <option value="300-500만원">300-500만원</option>
                                        <option value="500만원 이상">500만원 이상</option>
                                        <option value="협의 필요">협의 필요</option>
                                    </Select>
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="location">촬영 장소</Label>
                                    <Input type="text" id="location" name="location" value={formData.location} onChange={handleChange} placeholder="예: 서울 성수동 쇼룸, 미정" />
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="referenceUrl">참고 영상 URL</Label>
                                    <Input type="url" id="referenceUrl" name="referenceUrl" value={formData.referenceUrl} onChange={handleChange} placeholder="원하는 톤의 영상 링크" />
                                </FormGroup>
                            </>
                        )}

                        <FormGroup>
                            <Label htmlFor="message">메시지 *</Label>
                            <TextArea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="메시지를 입력해주세요" required />
                        </FormGroup>

                        <SubmitButton type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "전송 중..." : "메시지 보내기"}
                        </SubmitButton>
                    </ContactForm>
                </div>

                <div>
                    <ContactInfo>
                        <ContactInfoTitle>오시는 길</ContactInfoTitle>
                        <CompanyName>노온 스튜디오</CompanyName>
                        <InfoItem>
                            <InfoLabel>주소:</InfoLabel>
                            <InfoValue>서울특별시 광진구 자양번영로1길 37, 2층</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>TEL:</InfoLabel>
                            <InfoValue>+82-10-8485-7864</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>E-mail:</InfoLabel>
                            <InfoValue>noon.creatively@gmail.com</InfoValue>
                        </InfoItem>
                    </ContactInfo>

                    <MapContainer>
                        <MapTitle>오시는 길</MapTitle>
                        <MapContent>
                            <MapAddress>
                                서울특별시 광진구 자양번영로1길 37, 2층
                                <br />
                                (자양동, 노온 스튜디오)
                            </MapAddress>
                            <MapButtonContainer>
                                <MapButton
                                    href="https://map.kakao.com/link/search/서울특별시 광진구 자양번영로1길 37"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    카카오맵에서 보기
                                </MapButton>
                                <MapButton
                                    href="https://map.naver.com/v5/search/서울특별시 광진구 자양번영로1길 37"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    네이버지도에서 보기
                                </MapButton>
                                <MapButton
                                    href="https://www.google.com/maps/search/서울특별시+광진구+자양번영로1길+37"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Google Maps에서 보기
                                </MapButton>
                            </MapButtonContainer>
                        </MapContent>
                    </MapContainer>
                </div>
            </ContactContent>
        </ContactsContainer>
    );
};

export default Contacts;
